import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import EmailIcon from "@mui/icons-material/Email";
import GitHubIcon from "@mui/icons-material/GitHub";
import InstagramIcon from "@mui/icons-material/Instagram";
import LightModeIcon from "@mui/icons-material/LightMode";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import MenuIcon from "@mui/icons-material/Menu";
import TwitterIcon from "@mui/icons-material/Twitter";
import {
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Link as MuiLink,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme as useMuiTheme,
  Alert,
} from "@mui/material";
import { lazy, Suspense, useEffect, useState } from "react";
import CertificationsService from "../api/certificationsService";
import ProjectsService from "../api/projectsService";
import TechnologiesService from "../api/technologiesService";
import ConfigService from "../api/configService";
// Lazy load components that aren't needed immediately
const ProjectCard = lazy(() => import("../components/portfolio/ProjectCard"));
const CodeDisplayBox = lazy(() =>
  import("../components/portfolio/CodeDisplayBox")
);
import { useTheme } from "../contexts/ThemeContext";
import { getTechIconUrl } from "../utils/techIcons";
import { formatDate } from "../utils/helper";

// Create a placeholder loading component
const LoadingFallback = () => (
  <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
    <CircularProgress size={40} />
  </Box>
);

// Lazy load the contact form component
const ContactForm = lazy(() => {
  return new Promise((resolve) => {
    // Artificial delay to ensure this loads after critical content
    setTimeout(() => {
      resolve(import("../components/ContactForm"));
    }, 100);
  });
});

const HomePage = () => {
  const [projects, setProjects] = useState([]);
  const [technologies, setTechnologies] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [selectedTech, setSelectedTech] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { mode, toggleTheme } = useTheme();
  const isDarkMode = mode === "dark";
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [configValues, setConfigValues] = useState({
    heroTitle1: "FULL STACK",
    heroTitle2: "DEVELOPER",
    heroDescription:
      "I am Evan - an AWS Certified full-stack developer primarily focused on web development. I have extensive experience in leading various PHP, React, CSS, HTML, and Javascript development projects.",
    footerText: "© 2024 - All rights reserved. Built with React and NestJS.",
    contactTitle: "DO YOU HAVE A PROJECT TO DISCUSS?",
    portfolioTitle: "PORTFOLIO",
    skillsTitle: "SKILLS",
    certificationsTitle: "MY CERTIFICATIONS",
    linkedinUrl: "https://www.linkedin.com/in/evan-gentis/",
    githubUrl: "https://github.com/evangentis",
    email: "evan@evangentis.me",
  });

  // Track scroll position
  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Add scroll behavior to handle smooth scrolling
  useEffect(() => {
    // Apply smooth scrolling behavior to the document
    document.documentElement.style.scrollBehavior = "smooth";

    return () => {
      // Clean up when component unmounts
      document.documentElement.style.scrollBehavior = "";
    };
  }, []);

  // Function to handle smooth scrolling to sections
  const scrollToSection = (event, sectionId) => {
    event.preventDefault();
    const section = document.querySelector(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
    // Close mobile menu if open
    if (mobileMenuOpen) {
      handleMobileMenuClose();
    }
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  // Navigation menu items
  const navItems = [
    { label: "CERTIFICATIONS", href: "#certifications" },
    { label: "SKILLS", href: "#skills" },
    { label: "PORTFOLIO", href: "#portfolio" },
    { label: "CONTACT", href: "#contact" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [projectsData, technologiesData, certificationsData, configData] =
          await Promise.all([
            ProjectsService.getAll(),
            TechnologiesService.getAll(),
            CertificationsService.getAll(),
            ConfigService.getAllAsObject(),
          ]);
        setProjects(projectsData);
        setTechnologies(technologiesData);
        setCertifications(certificationsData);

        // Update config values with data from backend, keeping defaults if not found
        setConfigValues((prev) => ({
          ...prev,
          ...configData,
        }));
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleTechFilter = (techId) => {
    setSelectedTech(selectedTech === techId ? null : techId);
  };

  const filteredProjects = selectedTech
    ? projects.filter((project) =>
        project.technologies?.some((tech) => tech.id === selectedTech)
      )
    : projects;

  const activeProjects = filteredProjects.filter((project) => project.isActive);

  // Apply theme class to body
  useEffect(() => {
    document.body.className = isDarkMode ? "dark-mode" : "light-mode";
  }, [isDarkMode]);

  // Calculate nav background opacity based on scroll position
  // Start fading in at 20px of scroll, fully visible by 100px
  const calculateOpacity = () => {
    const threshold = 100; // When the opacity reaches 1
    const startFade = 20; // When the opacity starts to increase from 0

    if (scrollPosition <= startFade) return 0;
    if (scrollPosition >= threshold) return 1;

    return (scrollPosition - startFade) / (threshold - startFade);
  };

  const navOpacity = calculateOpacity();

  // Mobile drawer content
  const mobileDrawer = (
    <Box
      sx={{
        width: 250,
        height: "100%",
        backgroundColor: isDarkMode
          ? "rgba(18, 18, 41, 0.97)"
          : "rgba(255, 255, 255, 0.97)",
        backdropFilter: "blur(10px)",
        display: "flex",
        flexDirection: "column",
        p: 2,
      }}
      role="presentation"
    >
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 4 }}>
        <IconButton
          onClick={handleMobileMenuClose}
          sx={{ color: isDarkMode ? "white" : "#1a1a2e" }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <List sx={{ width: "100%", mt: 2 }}>
        {navItems.map((item) => (
          <ListItem key={item.label} disablePadding sx={{ mb: 2 }}>
            <ListItemButton
              onClick={(e) => scrollToSection(e, item.href)}
              sx={{
                textAlign: "center",
                py: 2,
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: isDarkMode
                    ? "rgba(245, 0, 87, 0.1)"
                    : "rgba(245, 0, 87, 0.05)",
                },
              }}
            >
              <Typography
                sx={{
                  width: "100%",
                  fontWeight: 600,
                  letterSpacing: "1px",
                  color: isDarkMode ? "white" : "#1a1a2e",
                }}
              >
                {item.label}
              </Typography>
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Box
        sx={{ mt: "auto", display: "flex", justifyContent: "center", py: 3 }}
      >
        <IconButton
          onClick={toggleTheme}
          sx={{
            color: isDarkMode ? "white" : "#1a1a2e",
            border: `1px solid ${
              isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)"
            }`,
            p: 2,
          }}
        >
          {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
        </IconButton>
      </Box>
    </Box>
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 8, textAlign: "center" }}>
        <Typography variant="h4" color="error" gutterBottom>
          {error}
        </Typography>
      </Container>
    );
  }

  return (
    <Box className={`bg-grid ${isDarkMode ? "dark-mode" : "light-mode"}`}>
      {/* Navigation */}
      <AppBar
        position="fixed"
        color="transparent"
        elevation={0}
        className={`${isDarkMode ? "dark" : "light"}`}
        sx={{
          top: { xs: 8, md: 16 },
          left: 0,
          right: 0,
          width: "100%",
          boxShadow: "none",
          background: "transparent",
          backgroundColor: "transparent",
          backgroundImage: "none",
          border: "none",
          zIndex: 1200,
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            display: "flex",
            justifyContent: isMobile ? "flex-end" : "center",
            background: "transparent",
            boxShadow: "none",
            px: { xs: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              backgroundColor: isMobile ? "transparent" : "transparent", // Keep transparent always - will add background overlay
              backdropFilter: isMobile
                ? "none"
                : navOpacity > 0
                ? "blur(10px)"
                : "none",
              borderRadius: "20px",
              border: "none", // Remove border initially
              position: "relative", // For the overlay
              px: { xs: 2, md: 4 },
              py: 1,
              display: "inline-flex",
              justifyContent: "center",
              boxShadow: "none", // Remove shadow initially
              width: isMobile ? "auto" : "auto",
              minWidth: isMobile ? "48px" : "auto",
              transition: "all 0.3s ease",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: "20px",
                backgroundColor: isDarkMode
                  ? `rgba(30, 30, 60, ${navOpacity * 0.7})`
                  : `rgba(255, 255, 255, ${navOpacity * 0.8})`,
                border: isDarkMode
                  ? `1px solid rgba(255, 255, 255, ${navOpacity * 0.05})`
                  : `1px solid rgba(0, 0, 0, ${navOpacity * 0.05})`,
                boxShadow: isDarkMode
                  ? `0 4px 20px rgba(0, 0, 0, ${navOpacity * 0.2})`
                  : `0 4px 20px rgba(0, 0, 0, ${navOpacity * 0.1})`,
                opacity: isMobile ? 0 : navOpacity, // Never show background for mobile
                transition:
                  "opacity 0.3s ease, background-color 0.3s ease, border 0.3s ease, box-shadow 0.3s ease",
                zIndex: -1,
                display: isMobile ? "none" : "block", // Hide completely for mobile
              },
            }}
          >
            <Toolbar
              disableGutters
              sx={{
                justifyContent: "center",
                background: "transparent",
                width: "100%",
                minHeight: isMobile ? "48px" : "64px",
              }}
            >
              {isMobile ? (
                // Mobile navigation with hamburger menu
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <IconButton
                    color="inherit"
                    aria-label="open menu"
                    edge="end"
                    onClick={handleMobileMenuToggle}
                    sx={{
                      color: isDarkMode ? "white" : "#1a1a2e",
                      padding: 1,
                      backgroundColor: isDarkMode
                        ? "rgba(30, 30, 60, 0.7)"
                        : "rgba(255, 255, 255, 0.8)",
                    }}
                  >
                    <MenuIcon fontSize={isMobile ? "medium" : "large"} />
                  </IconButton>
                </Box>
              ) : (
                // Desktop navigation
                <Box sx={{ display: "flex", gap: 3, alignItems: "center" }}>
                  {navItems.map((item) => (
                    <MuiLink
                      key={item.label}
                      href={item.href}
                      onClick={(e) => scrollToSection(e, item.href)}
                      className={`nav-link ${isDarkMode ? "dark" : "light"}`}
                    >
                      {item.label}
                    </MuiLink>
                  ))}
                  <IconButton
                    onClick={toggleTheme}
                    sx={{
                      color: isDarkMode ? "white" : "#1a1a2e",
                    }}
                  >
                    {isDarkMode ? (
                      <LightModeIcon fontSize="small" />
                    ) : (
                      <DarkModeIcon fontSize="small" />
                    )}
                  </IconButton>
                </Box>
              )}
            </Toolbar>
          </Box>
        </Container>
      </AppBar>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        PaperProps={{
          sx: {
            //width: "75%",
            maxWidth: 280,
            borderTopLeftRadius: 20,
            borderBottomLeftRadius: 20,
            backgroundColor: "transparent",
            boxShadow: isDarkMode
              ? "0 0 25px rgba(0, 0, 0, 0.5)"
              : "0 0 25px rgba(0, 0, 0, 0.2)",
            border: "none",
            transition: "all 0.3s ease-in-out",
          },
        }}
        sx={{
          "& .MuiBackdrop-root": {
            backgroundColor: isDarkMode
              ? "rgba(0, 0, 0, 0.7)"
              : "rgba(0, 0, 0, 0.3)",
            backdropFilter: "blur(3px)",
          },
        }}
      >
        {mobileDrawer}
      </Drawer>

      {/* Hero Section */}
      <Box className={`hero-section ${isDarkMode ? "dark" : "light"}`}>
        <Box className={`animated-bg ${isDarkMode ? "dark" : "light"}`} />
        <Container maxWidth="lg">
          <Grid
            container
            spacing={4}
            alignItems="center"
            sx={{ minHeight: "85vh" }}
          >
            <Grid
              item
              size={{ xs: 12, md: 6 }}
              justifyContent={{ xs: "center", md: "flex-start" }}
              sx={{ textAlign: { xs: "center", md: "left" } }}
            >
              <Box className="hero-content">
                <Typography
                  variant="h1"
                  color="primary"
                  gutterBottom
                  sx={{
                    fontSize: { xs: "2.5rem", md: "3.5rem" },
                    fontWeight: 800,
                    letterSpacing: "0.5px",
                    mb: 1,
                    mt: { xs: 12, md: 0 },
                  }}
                >
                  {configValues.heroTitle1}
                </Typography>
                <Typography
                  variant="h1"
                  color="primary"
                  gutterBottom
                  sx={{
                    fontSize: { xs: "2.5rem", md: "3.5rem" },
                    fontWeight: 800,
                    letterSpacing: "0.5px",
                    mb: 4,
                  }}
                >
                  {configValues.heroTitle2}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    maxWidth: { xs: "100%", md: "500px" },
                    mb: 4,
                  }}
                >
                  {configValues.heroDescription}
                </Typography>
                <Button
                  variant="contained"
                  className="view-btn"
                  href="#portfolio"
                  onClick={(e) => scrollToSection(e, "#portfolio")}
                  sx={{
                    px: 3,
                    py: 1,
                    borderRadius: 2,
                    fontWeight: 600,
                    fontSize: "1rem",
                  }}
                >
                  VIEW MY WORK
                </Button>
              </Box>
            </Grid>
            <Grid
              item
              size={{ xs: 12, md: 6 }}
              sx={{ mb: { xs: 12, md: 0 }, position: "relative" }}
            >
              {/* Wrap CodeDisplayBox in Suspense for lazy loading */}
              <Suspense fallback={<LoadingFallback />}>
                <CodeDisplayBox
                  isDarkMode={isDarkMode}
                  projects={projects}
                  technologies={technologies}
                  toggleTheme={toggleTheme}
                  configValues={configValues}
                />
              </Suspense>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Certifications Section */}
      <Box id="certifications" sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h2"
            className="section-title"
            color={isDarkMode ? "white" : "#1a1a2e"}
            sx={{
              fontSize: { xs: "1.75rem", md: "2.25rem" },
              fontWeight: 700,
              mb: 6,
            }}
          >
            {configValues.certificationsTitle}
          </Typography>

          <Grid container spacing={4}>
            {certifications.map((certification) => (
              <Grid item size={{ xs: 12, md: 6 }} key={certification.id}>
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    height: "100%",
                    background: isDarkMode
                      ? "rgba(18, 18, 41, 0.3)"
                      : "rgba(255, 255, 255, 0.7)",
                    border: isDarkMode
                      ? "1px solid rgba(255, 255, 255, 0.05)"
                      : "1px solid rgba(0, 0, 0, 0.05)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: isDarkMode
                        ? "0 10px 25px rgba(0, 0, 0, 0.2)"
                        : "0 10px 25px rgba(0, 0, 0, 0.1)",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 3,
                        background: "rgba(245, 0, 87, 0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 2,
                        p: 3,
                      }}
                    >
                      <img
                        src={certification.icon}
                        alt={certification.name}
                        style={{ width: 40, height: 40 }}
                      />
                    </Box>
                    <Typography variant="h6" component="h3" fontWeight={600}>
                      {certification.name}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500 }}
                  >
                    Obtained: {formatDate(certification.obtainedDate)}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Skills Section */}
      <Box
        id="skills"
        className={isDarkMode ? "dark-section" : "light-section"}
        sx={{ py: 10 }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h2"
            className="section-title"
            color={isDarkMode ? "white" : "#1a1a2e"}
            sx={{
              fontSize: { xs: "1.75rem", md: "2.25rem" },
              fontWeight: 700,
              mb: 6,
            }}
          >
            {configValues.skillsTitle}
          </Typography>

          <Grid container spacing={3} justifyContent="center">
            {technologies.map((tech) => (
              <Grid item key={tech.id}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    background: isDarkMode
                      ? "rgba(18, 18, 41, 0.3)"
                      : "rgba(255, 255, 255, 0.7)",
                    border: isDarkMode
                      ? "1px solid rgba(255, 255, 255, 0.05)"
                      : "1px solid rgba(0, 0, 0, 0.05)",
                    p: 2,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: isDarkMode
                        ? "0 10px 25px rgba(0, 0, 0, 0.2)"
                        : "0 10px 25px rgba(0, 0, 0, 0.1)",
                      border: isDarkMode
                        ? "1px solid rgba(245, 0, 87, 0.3)"
                        : "1px solid rgba(245, 0, 87, 0.2)",
                    },
                  }}
                >
                  {tech.icon ? (
                    <img
                      src={tech.icon}
                      alt={tech.name}
                      style={{ width: 40, height: 40, marginBottom: 8 }}
                    />
                  ) : (
                    // Try to get icon from our techIcons mapping
                    (() => {
                      const iconUrl = getTechIconUrl(tech.name);
                      return iconUrl ? (
                        <img
                          src={iconUrl}
                          alt={tech.name}
                          style={{ width: 40, height: 40, marginBottom: 8 }}
                        />
                      ) : (
                        // Fallback to first letter if no icon is found
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            background: "rgba(245, 0, 87, 0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 1,
                          }}
                        >
                          {tech.name.charAt(0)}
                        </Box>
                      );
                    })()
                  )}
                  <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                    {tech.name}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Portfolio Section */}
      <Box id="portfolio" sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h2"
            className="section-title"
            color={isDarkMode ? "white" : "#1a1a2e"}
            sx={{
              fontSize: { xs: "1.75rem", md: "2.25rem" },
              fontWeight: 700,
              mb: 6,
            }}
          >
            {configValues.portfolioTitle}
          </Typography>

          {/* Technology filters */}
          {technologies.length > 0 && (
            <Box sx={{ mb: 5 }}>
              <Typography
                variant="h6"
                gutterBottom
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                Filter by technology:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {technologies.map((tech) => (
                  <Chip
                    key={tech.id}
                    label={tech.name}
                    onClick={() => handleTechFilter(tech.id)}
                    color={selectedTech === tech.id ? "primary" : "default"}
                    className={`tech-chip ${isDarkMode ? "dark" : "light"} ${
                      selectedTech === tech.id ? "active" : ""
                    }`}
                    sx={{ m: 0.5 }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Projects grid with lazy loading */}
          {activeProjects.length > 0 ? (
            <Grid container spacing={4} sx={{ width: "100%" }}>
              {activeProjects.map((project) => (
                <Grid
                  item
                  key={project.id}
                  size={{ xs: 12, sm: 6, md: 4 }}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      //maxWidth: { xs: "100%", sm: "320px", md: "320px" },
                    }}
                  >
                    {/* Wrap each ProjectCard in Suspense for lazy loading */}
                    <Suspense fallback={<LoadingFallback />}>
                      <ProjectCard project={project} isDarkMode={isDarkMode} />
                    </Suspense>
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography
              variant="h5"
              sx={{ textAlign: "center", my: 10, color: "text.secondary" }}
            >
              {selectedTech
                ? "No projects found with the selected technology."
                : "No projects found."}
            </Typography>
          )}
        </Container>
      </Box>

      {/* Contact Section */}
      <Box
        id="contact"
        className={isDarkMode ? "dark-section" : "light-section"}
        sx={{ py: 10 }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h2"
            className="section-title"
            color={isDarkMode ? "white" : "#1a1a2e"}
            sx={{
              fontSize: { xs: "1.75rem", md: "2.25rem" },
              fontWeight: 700,
              mb: 6,
            }}
          >
            {configValues.contactTitle}
          </Typography>

          <Grid container spacing={6}>
            {/* Contact Information - Left Column */}
            <Grid item size={{ xs: 12, sm: 12, md: 6 }}>
              <Typography
                variant="h5"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 4,
                  color: isDarkMode ? "white" : "#1a1a2e",
                }}
              >
                GET IN TOUCH <ArrowForwardIcon sx={{ ml: 1 }} />
              </Typography>

              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  color={isDarkMode ? "white" : "#1a1a2e"}
                >
                  CONTACT
                </Typography>
                <MuiLink
                  href={`mailto:${configValues.email}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    color: "text.secondary",
                    textDecoration: "none",
                    transition: "color 0.3s ease",
                    "&:hover": {
                      color: "primary.main",
                    },
                  }}
                >
                  <EmailIcon sx={{ mr: 1 }} fontSize="small" />
                  {configValues.email}
                </MuiLink>
              </Box>

              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                  color={isDarkMode ? "white" : "#1a1a2e"}
                >
                  SOCIAL MEDIA
                </Typography>
                <Stack direction="row" spacing={2}>
                  <IconButton
                    className={`social-icon ${isDarkMode ? "dark" : "light"}`}
                    href={configValues.linkedinUrl}
                    target="_blank"
                  >
                    <LinkedInIcon />
                  </IconButton>
                  <IconButton
                    className={`social-icon ${isDarkMode ? "dark" : "light"}`}
                    href={configValues.githubUrl}
                    target="_blank"
                  >
                    <GitHubIcon />
                  </IconButton>
                </Stack>
              </Box>
            </Grid>

            {/* Contact Form - Right Column */}
            <Grid
              item
              size={{ xs: 12, sm: 12, md: 6 }}
              sx={{ maxWidth: "600px" }}
            >
              <Suspense fallback={<LoadingFallback />}>
                <ContactForm isDarkMode={isDarkMode} />
              </Suspense>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        className={`footer ${isDarkMode ? "dark" : "light"}`}
        sx={{ py: 3, textAlign: "center" }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary">
            {configValues.footerText}
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;
